import type {
  ChangeEvent,
  ComponentProps,
  FormEvent,
  KeyboardEvent,
  PropsWithChildren,
} from "react";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  CornerDownLeftIcon,
  CheckIcon,
  Loader2Icon,
  MicIcon,
  MicOffIcon,
  PaperclipIcon,
  SquareIcon,
  XIcon,
} from "lucide-react";
import { LiveWaveform } from "./live-waveform";
import { nanoid } from "nanoid";
import { cn } from "./lib/cn";
import { useSpeechToText, SPEECH_TO_TEXT_STATUS } from "./lib/use-speech-to-text";
import type { ChatStatus, FileUIPart } from "./types";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
} from "./ui/input-group";

type Attachment = FileUIPart & { id: string };
export type AttachmentsContext = {
  files: Attachment[];
  add: (files: File[] | FileList) => void;
  remove: (id: string) => void;
  clear: () => void;
  openFileDialog: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
};
export type TextInputContext = {
  value: string;
  setInput: (value: string) => void;
  clear: () => void;
};
export type PromptInputControllerProps = {
  textInput: TextInputContext;
  attachments: AttachmentsContext;
};
const ControllerContext = createContext<PromptInputControllerProps | null>(null);
export const usePromptInputController = () => {
  const context = useContext(ControllerContext);
  if (!context) {
    throw new Error("PromptInput components require PromptInputProvider.");
  }
  return context;
};
export type PromptInputProviderProps = PropsWithChildren<{ initialInput?: string }>;
export const PromptInputProvider = ({ initialInput = "", children }: PromptInputProviderProps) => {
  const [value, setValue] = useState(initialInput);
  const [files, setFiles] = useState<Attachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const add = useCallback((nextFiles: File[] | FileList) => {
    const incoming = Array.from(nextFiles);
    if (incoming.length === 0) {
      return;
    }
    setFiles((current) =>
      current.concat(
        incoming.map((file) => ({
          id: nanoid(),
          type: "file",
          url: URL.createObjectURL(file),
          mediaType: file.type,
          filename: file.name,
        })),
      ),
    );
  }, []);
  const remove = useCallback(
    (id: string) =>
      setFiles((current) => {
        const attachment = current.find((file) => file.id === id);
        if (attachment) {
          URL.revokeObjectURL(attachment.url);
        }
        return current.filter((file) => file.id !== id);
      }),
    [],
  );
  const clear = useCallback(
    () =>
      setFiles((current) => {
        current.forEach((file) => URL.revokeObjectURL(file.url));
        return [];
      }),
    [],
  );
  const attachments = useMemo<AttachmentsContext>(
    () => ({
      files,
      add,
      remove,
      clear,
      openFileDialog: () => fileInputRef.current?.click(),
      fileInputRef,
    }),
    [files, add, remove, clear],
  );
  const controller = useMemo<PromptInputControllerProps>(
    () => ({ textInput: { value, setInput: setValue, clear: () => setValue("") }, attachments }),
    [value, attachments],
  );
  return <ControllerContext.Provider value={controller}>{children}</ControllerContext.Provider>;
};

export type PromptInputProps = ComponentProps<"form"> & {
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
};
/**
 * Application-compatible subset of the current AI Elements prompt form.
 *
 * The upstream attachment menus depend on unvendored command, dropdown, and select primitives;
 * this package intentionally retains the existing controlled composer contract used by AgentChat.
 */
export const PromptInput = ({ className, onSubmit, ...props }: PromptInputProps) => (
  <form className={cn("w-full", className)} onSubmit={onSubmit} {...props} />
);
/**
 * Surface treatments for the composer shell.
 *
 * `raised` is for composers that sit on a decorated backdrop rather than a flat page: it takes an
 * opaque fill and a heavier edge so the field stays legible over whatever is behind it, and a
 * softer corner radius to match a more informal surface.
 */
export const promptInputBodyVariants = cva("", {
  variants: {
    variant: {
      default: "",
      raised: cn(
        "rounded-2xl border-2 bg-background shadow-sm dark:bg-background",
        // Larger type for a composer this prominent; the placeholder inherits it.
        "[&_[data-slot=input-group-control]]:text-base",
        // The shared focus treatment paints the ring token, which the host maps to its primary
        // colour. On this surface that reads as an unrelated blue highlight, so focus stays
        // visible through a darkened edge and a neutral ring instead.
        "has-[[data-slot=input-group-control]:focus-visible]:border-foreground/30",
        "has-[[data-slot=input-group-control]:focus-visible]:ring-foreground/10",
      ),
    },
  },
  defaultVariants: { variant: "default" },
});

export type PromptInputBodyProps = ComponentProps<typeof InputGroup> &
  VariantProps<typeof promptInputBodyVariants>;

export const PromptInputBody = ({ className, variant, ...props }: PromptInputBodyProps) => (
  <InputGroup className={cn(promptInputBodyVariants({ variant }), className)} {...props} />
);
export const PromptInputTextarea = forwardRef<
  HTMLTextAreaElement,
  ComponentProps<typeof InputGroupTextarea>
>(({ onChange, onKeyDown, ...props }, ref) => {
  const controller = useContext(ControllerContext);
  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    controller?.textInput.setInput(event.target.value);
    onChange?.(event);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing &&
      event.keyCode !== 229
    ) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
    onKeyDown?.(event);
  };
  return (
    <InputGroupTextarea
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      ref={ref}
      value={controller?.textInput.value}
      {...props}
    />
  );
});
PromptInputTextarea.displayName = "PromptInputTextarea";
export const PromptInputToolbar = (props: ComponentProps<typeof InputGroupAddon>) => (
  <InputGroupAddon align="block-end" {...props} />
);
export const PromptInputTools = (props: ComponentProps<"div">) => (
  <div className={cn("flex items-center gap-1", props.className)} {...props} />
);
export const PromptInputSubmit = ({
  status,
  className,
  children,
  disabled,
  ...props
}: ComponentProps<typeof InputGroupButton> & { status?: ChatStatus }) => {
  const streaming = status === "streaming";
  const Icon = streaming
    ? SquareIcon
    : status === "submitted"
      ? Loader2Icon
      : status === "error"
        ? XIcon
        : CornerDownLeftIcon;
  return (
    <InputGroupButton
      aria-label={streaming ? "Stop" : "Submit"}
      className={cn(className, status === "submitted" && "animate-spin")}
      disabled={disabled || status === "submitted"}
      size="icon-sm"
      type="submit"
      {...props}
    >
      {children ?? <Icon className="size-4" />}
    </InputGroupButton>
  );
};
export type PromptInputAttachButtonProps = Omit<
  ComponentProps<typeof InputGroupButton>,
  "children"
> & {
  /** Localized accessible name for the control. */
  label: string;
};
/**
 * Toolbar affordance for adding attachments.
 *
 * The icon lives here rather than in the caller so every AI Elements surface keeps one icon set,
 * and so host applications need no icon dependency of their own.
 */
export const PromptInputAttachButton = ({ label, ...props }: PromptInputAttachButtonProps) => (
  <InputGroupButton aria-label={label} size="icon-sm" type="button" {...props}>
    <PaperclipIcon className="size-4" />
  </InputGroupButton>
);
export const PromptInputAttachmentInput = ({ onChange, ...props }: ComponentProps<"input">) => {
  const controller = usePromptInputController();
  return (
    <input
      className="sr-only"
      multiple
      onChange={(event) => {
        controller.attachments.add(event.target.files ?? []);
        onChange?.(event);
      }}
      ref={controller.attachments.fileInputRef}
      type="file"
      {...props}
    />
  );
};

const appendTranscript = (existingValue: string, transcript: string) => {
  const trimmedTranscript = transcript.trim();
  if (!trimmedTranscript) {
    return existingValue;
  }
  const needsLeadingSpace = existingValue.length > 0 && !/\s$/.test(existingValue);
  return `${existingValue}${needsLeadingSpace ? " " : ""}${trimmedTranscript} `;
};

export type PromptInputSpeechButtonLabels = {
  start: string;
  stop: string;
  unsupported: string;
  error: string;
};

export type PromptInputSpeechStartButtonProps = Omit<
  ComponentProps<typeof InputGroupButton>,
  "children"
> & {
  label: string;
  supported: boolean;
  unsupportedLabel: string;
};

/** Controlled microphone trigger for composers that own their full recording layout. */
export const PromptInputSpeechStartButton = ({
  label,
  supported,
  unsupportedLabel,
  disabled,
  ...props
}: PromptInputSpeechStartButtonProps) => (
  <InputGroupButton
    {...props}
    aria-label={supported ? label : unsupportedLabel}
    disabled={!supported || disabled}
    size="icon-sm"
    title={supported ? label : unsupportedLabel}
    type="button"
  >
    {supported ? <MicIcon className="size-4" /> : <MicOffIcon className="size-4" />}
  </InputGroupButton>
);

const DEFAULT_SPEECH_BUTTON_LABELS: PromptInputSpeechButtonLabels = {
  start: "Start voice input",
  stop: "Stop voice input",
  unsupported: "Voice input is not available in this browser",
  error: "Could not hear you. Try again",
};

export type PromptInputSpeechButtonProps = Omit<
  ComponentProps<typeof InputGroupButton>,
  "aria-label" | "onChange"
> & {
  /** BCP 47 language tag passed to the recognizer. Defaults to the browser's language. */
  lang?: string;
  /** Translated labels for each state. Falls back to English copy for callers that don't localize. */
  labels?: Partial<PromptInputSpeechButtonLabels>;
  /** Current textarea value, so a final transcript appends instead of replacing what's typed. */
  value: string;
  /** Called with the input value after a final transcript is appended. */
  onValueChange: (value: string) => void;
};

/**
 * Microphone toggle that dictates into a prompt textarea using the Web Speech API.
 *
 * Controlled the same way as {@link PromptInputTextarea} (`value` / `onValueChange`) rather than
 * through {@link PromptInputProvider}, since callers such as AgentChat own the composer draft
 * directly and don't mount the provider. Finalized speech appends to any text already typed
 * rather than replacing it, so a user can mix typing and dictation in one message. The button is
 * disabled (not hidden) with an explanatory label when the browser has no speech recognition
 * support, and switches to the destructive color while recording or after an error so the state
 * is never ambiguous.
 */
export const PromptInputSpeechButton = ({
  lang,
  labels,
  value,
  onValueChange,
  className,
  disabled,
  ...props
}: PromptInputSpeechButtonProps) => {
  const resolvedLabels = { ...DEFAULT_SPEECH_BUTTON_LABELS, ...labels };
  const handleFinalTranscript = useCallback(
    (transcript: string) => {
      onValueChange(appendTranscript(value, transcript));
    },
    [onValueChange, value],
  );
  const { isSupported, status, toggle } = useSpeechToText({
    lang,
    onFinalTranscript: handleFinalTranscript,
  });
  const isRecording =
    status === SPEECH_TO_TEXT_STATUS.ARMING || status === SPEECH_TO_TEXT_STATUS.RECORDING;
  const hasError = status === SPEECH_TO_TEXT_STATUS.ERROR;

  const label = !isSupported
    ? resolvedLabels.unsupported
    : isRecording
      ? resolvedLabels.stop
      : hasError
        ? resolvedLabels.error
        : resolvedLabels.start;

  return (
    <InputGroupButton
      aria-label={label}
      aria-pressed={isRecording}
      className={cn(
        isRecording && "text-destructive",
        hasError && !isRecording && "text-destructive/70",
        className,
      )}
      disabled={disabled || !isSupported}
      onClick={toggle}
      size="icon-sm"
      title={label}
      type="button"
      {...props}
    >
      {isSupported ? <MicIcon className="size-4" /> : <MicOffIcon className="size-4" />}
    </InputGroupButton>
  );
};

export type PromptInputSpeechRecordingProps = {
  active: boolean;
  cancelLabel: string;
  completeLabel: string;
  processing?: boolean;
  onCancel: () => void;
  onComplete: () => void;
};

/** Focused recording state for a prompt: cancel, live microphone waveform, and complete. */
export const PromptInputSpeechRecording = ({
  active,
  cancelLabel,
  completeLabel,
  processing = false,
  onCancel,
  onComplete,
}: PromptInputSpeechRecordingProps) => {
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Synchronizes recording shortcuts with browser keyboard events.
  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (processing) {
        return;
      }
      if (event.key !== "Escape" && event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (event.key === "Escape") {
        onCancel();
      } else {
        onComplete();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onCancel, onComplete, processing]);

  return (
    <div className="flex h-8 w-full items-center gap-2">
      <InputGroupButton
        aria-label={cancelLabel}
        className="shrink-0 text-destructive"
        disabled={processing}
        onClick={onCancel}
        size="icon-sm"
        type="button"
      >
        <XIcon className="size-4" />
      </InputGroupButton>
      {active ? (
        <LiveWaveform
          active
          barHeight={4}
          className="min-w-0 flex-1 text-foreground"
          height={28}
          historySize={240}
          mode="scrolling"
          sensitivity={3}
        />
      ) : (
        <div className="flex h-7 min-w-0 flex-1 items-center justify-center text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" />
        </div>
      )}
      <InputGroupButton
        aria-label={completeLabel}
        className="shrink-0 text-foreground"
        disabled={processing}
        onClick={onComplete}
        size="icon-sm"
        type="button"
      >
        {processing ? (
          <Loader2Icon className="size-4 animate-spin" />
        ) : (
          <CheckIcon className="size-4" />
        )}
      </InputGroupButton>
    </div>
  );
};
